import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { SignInPage } from './SignInPage'
import signIn from './SignInPage.module.css'
import styles from './NoAccessPage.module.css'

// Invite system user (routes/invites.js). Signed out, the link is the sign-in screen saying what the invitation is
// for; once they sign in, WorkspaceContext takes it up before anything else is asked, and only a refusal lands here.

interface Preview {
  workspace: string
  permission_set: string
  invited_email: string | null
  invited_by: string | null
  expires_at: string
  status: 'open' | 'expired'
}

const GONE = 'This invitation has already been used, was withdrawn, or the link is incomplete. Ask whoever invited you to send it again.'

export function InviteSignIn() {
  const { token = '' } = useParams()
  const [preview, setPreview] = useState<Preview | null>(null)
  const [gone, setGone] = useState<string | null>(null)

  useEffect(() => {
    let live = true
    // Plain fetch: nobody is signed in yet, and the token is the whole question.
    fetch(`/api/invite/${encodeURIComponent(token)}`, { credentials: 'omit', referrerPolicy: 'no-referrer' })
      .then(async (r) => {
        const body = await r.json().catch(() => null)
        if (!live) return
        if (r.ok) setPreview(body)
        else setGone(r.status === 404 ? GONE : 'The invitation could not be checked just now. You can still sign in.')
      })
      .catch(() => live && setGone('The invitation could not be checked just now. You can still sign in.'))
    return () => {
      live = false
    }
  }, [token])

  if (preview && preview.status === 'open') {
    return (
      <SignInPage
        title={`Join ${preview.workspace}`}
        lead={
          <>
            {preview.invited_by || 'Your administrator'} invited you to CONTROL with <strong>{preview.permission_set}</strong> access. Sign in
            with any of these accounts to accept. It becomes your sign-in for CONTROL.
          </>
        }
        notice={
          <div className={signIn.invite}>
            This is for CONTROL, the office system. The link works once, until{' '}
            {new Date(preview.expires_at).toLocaleString(undefined, { weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })}.
          </div>
        }
      />
    )
  }
  return (
    <SignInPage
      notice={
        preview || gone ? (
          <div className={signIn.inviteGone} role="alert">
            {preview ? 'This invitation ran out after a day. Ask whoever invited you to send it again.' : gone}
          </div>
        ) : null
      }
    />
  )
}

// Signed in, and the link was refused: say why, and let them carry on to whatever access they already have.
export function InviteProblemPage({ message, canContinue }: { message: string; canContinue: boolean }) {
  const { user, signOut } = useAuth()
  return (
    <div className={styles.screen} data-screen-label="Invitation">
      <div className={styles.glowWarm} aria-hidden="true" />
      <div className={styles.glowSage} aria-hidden="true" />
      <div className={styles.brand}>
        <span className={styles.brandName}>CONTROL</span>
      </div>
      <div className={styles.content}>
        <h1 className={styles.title}>The invitation did not go through</h1>
        <p className={styles.lead}>{message}</p>
        <section className={styles.panel} aria-labelledby="invite-signed-in-as">
          <div id="invite-signed-in-as" className={styles.panelLabel}>
            You signed in as
          </div>
          <div className={styles.identity}>
            <span className={styles.email}>{user?.email || 'your account'}</span>
          </div>
        </section>
        <div className={styles.actions}>
          {canContinue ? (
            <a href="/" className={styles.primary}>
              Continue to CONTROL
            </a>
          ) : (
            <button type="button" onClick={() => signOut().catch(() => {})} className={styles.primary}>
              Try a different account
            </button>
          )}
          <a href="mailto:support@dataphyll.com" className={styles.secondary}>
            Email support@dataphyll.com
          </a>
        </div>
      </div>
    </div>
  )
}
